// src/services/emailService.js - FIXED VERSION
import { doc, updateDoc, addDoc, collection, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import emailjs from '@emailjs/browser';

const EMAILJS_CONFIG = {
  SERVICE_ID: 'service_wwcl1pf',
  TEMPLATE_ID_QUOTE_CUSTOMER: 'template_vf6btpf',
  TEMPLATE_ID_DRIVER_ASSIGNMENT: 'template_cisycii',
  PUBLIC_KEY: 'vQi7kKfPXGUY9DF-i'
};

const BUSINESS_EMAILS = {
  FROM_EMAIL: 'contact@flexlogisticsmanagement.com',
  ADMIN_EMAIL: 'contact@flexlogisticsmanagement.com',
  SUPPORT_EMAIL: 'contact@flexlogisticsmanagement.com'
};

// Initialize EmailJS
emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);

// ✅ ENHANCED: Driver assignment notification with SMS/Email options
export const sendDriverAssignment = async (driver, shipment, signatureLink, notificationMethod = 'email') => {
  try {
    console.log('🚚 SENDING DRIVER ASSIGNMENT NOTIFICATION');
    console.log('Driver:', driver.name);
    console.log('Notification Method:', notificationMethod);
    console.log('Shipment:', shipment.displayId || shipment.id);
    
    let emailSent = false;
    let smsSent = false;

    // Send email if method includes email
    if (notificationMethod === 'email' || notificationMethod === 'both') {
      emailSent = await sendDriverAssignmentEmail(driver, shipment, signatureLink);
    }

    // Send SMS if method includes SMS
    if (notificationMethod === 'sms' || notificationMethod === 'both') {
      smsSent = await sendDriverAssignmentSMS(driver, shipment, signatureLink);
    }

    console.log('✅ DRIVER NOTIFICATION COMPLETE');
    console.log('Email sent:', emailSent);
    console.log('SMS sent:', smsSent);
    
    return { emailSent, smsSent };

  } catch (error) {
    console.error('❌ DRIVER ASSIGNMENT NOTIFICATION FAILED:', error);
    return { emailSent: false, smsSent: false };
  }
};

// ✅ ULTRA-SIMPLE: Only 3 guaranteed variables
const sendDriverAssignmentEmail = async (driver, shipment, signatureLink) => {
  try {
    console.log('📧 SENDING ULTRA-SIMPLE EMAIL');
    
    const templateParams = {
      // EmailJS system parameters
      to_email: driver.email,
      to_name: driver.name,
      from_name: 'Flex Logistics',
      from_email: BUSINESS_EMAILS.FROM_EMAIL,
      reply_to: BUSINESS_EMAILS.SUPPORT_EMAIL,
      subject: `New Delivery - ${shipment.displayId || shipment.id}`,
      
      // ✅ ONLY 3 VARIABLES - Guaranteed to exist
      driver_name: driver.name,
      shipment_id: shipment.displayId || shipment.id,
      customer_name: shipment.userName || 'Customer',
      signature_link: signatureLink
    };

    console.log('🔍 Sending with only 3 variables:', {
      driver_name: templateParams.driver_name,
      shipment_id: templateParams.shipment_id,
      customer_name: templateParams.customer_name
    });

    const result = await emailjs.send(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.TEMPLATE_ID_DRIVER_ASSIGNMENT,
      templateParams
    );

    console.log('✅ ULTRA-SIMPLE EMAIL SENT SUCCESSFULLY');
    return true;

  } catch (error) {
    console.error('❌ ULTRA-SIMPLE EMAIL FAILED:');
    console.error('Full error:', error);
    
    // Last resort fallback
    console.log('🔄 Trying mailto fallback...');
    const subject = `Delivery Assignment - ${shipment.displayId || shipment.id}`;
    const body = `Hi ${driver.name}, you have a new delivery. Signature: ${signatureLink}`;
    window.open(`mailto:${driver.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
    
    return false;
  }
};

// ✅ SIMPLIFIED: SMS notification function (No API calls)
const sendDriverAssignmentSMS = async (driver, shipment, signatureLink) => {
  try {
    if (!driver.phone) {
      console.log('❌ No phone number available for SMS');
      alert(`No phone number for ${driver.name}. Please add a phone number or use email notification.`);
      return false;
    }

    console.log('📱 PREPARING DRIVER ASSIGNMENT SMS');
    console.log('Driver Phone:', driver.phone);
    
    // Create SMS message with shipment details
    const smsMessage = `FLEX LOGISTICS - New Shipment\n\nID: ${shipment.displayId || shipment.id}\nPickup: ${shortenAddress(shipment.pickupAddress)}\nDelivery: ${shortenAddress(shipment.dropoffAddress)}\nCustomer: ${shipment.userName}\n\nSIGNATURE LINK: ${signatureLink}\n\nReply STOP to unsubscribe`;
    
    // Show instructions to admin for manual SMS sending
    const userConfirmed = window.confirm(
      `SMS READY FOR ${driver.name} (${driver.phone})\n\n` +
      `Message: ${smsMessage}\n\n` +
      `Click OK to copy message to clipboard, then send it manually via your phone.` +
      `\n\nOr click Cancel to skip SMS.`
    );

    if (userConfirmed) {
      // Copy to clipboard
      await navigator.clipboard.writeText(smsMessage);
      alert(`✅ SMS message copied to clipboard!\n\nPlease send it to: ${driver.phone}\n\nMessage is ready in your clipboard.`);
      console.log('📋 SMS message copied to clipboard for manual sending');
      return true;
    } else {
      console.log('❌ User cancelled SMS sending');
      return false;
    }

  } catch (error) {
    console.error('❌ SMS preparation failed:', error);
    
    // Fallback: Show manual instructions
    alert(`SMS preparation failed. Please manually text this link to ${driver.phone}:\n\n${signatureLink}`);
    return false;
  }
};

// Helper function to shorten addresses for SMS
const shortenAddress = (address) => {
  if (!address) return 'Address not provided';
  // Take first 30 characters of address
  return address.length > 30 ? address.substring(0, 30) + '...' : address;
};

// ✅ Fallback email method using mailto
const sendFallbackDriverEmail = async (driver, shipment, signatureLink) => {
  try {
    const subject = `New Shipment Assignment - ${shipment.displayId || shipment.id}`;
    const body = `
Hello ${driver.name},

You have been assigned to handle a shipment.

SHIPMENT DETAILS:
- Shipment ID: ${shipment.displayId || shipment.id}
- Item: ${shipment.itemDescription}
- Customer: ${shipment.userName} (${shipment.phone})
- Pickup: ${shipment.pickupAddress}
- Delivery: ${shipment.dropoffAddress}
${shipment.specialInstructions ? `- Special Instructions: ${shipment.specialInstructions}` : ''}

YOUR SIGNATURE LINK:
${signatureLink}

INSTRUCTIONS:
1. Click the signature link when you arrive for pickup
2. Have the customer sign for pickup confirmation
3. Click the same link again when you complete delivery
4. Have the customer sign for delivery confirmation

Vehicle: ${driver.vehicleInfo}
License: ${driver.licenseNumber}

Thank you,
Flex Logistics Management Team
    `.trim();

    const mailtoLink = `mailto:${driver.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open default email client
    window.open(mailtoLink, '_blank');
    
    console.log('📧 FALLBACK EMAIL INITIATED VIA MAILTO');
    return true;
  } catch (error) {
    console.error('❌ FALLBACK EMAIL FAILED:', error);
    
    // Last resort: Just copy the signature link to clipboard
    navigator.clipboard.writeText(signatureLink).then(() => {
      alert(`Signature link copied to clipboard. Please send it to ${driver.name} manually.`);
    });
    
    return false;
  }
};

// ✅ UPDATED: Admin notification with safe variable names
export const notifyAdminDriverAssignment = async (driver, shipment) => {
  try {
    console.log('📋 SENDING ADMIN NOTIFICATION');
    
    const emailParams = {
      to_email: BUSINESS_EMAILS.ADMIN_EMAIL,
      to_name: 'Admin Team',
      from_name: 'Flex Logistics System',
      from_email: BUSINESS_EMAILS.FROM_EMAIL,
      reply_to: BUSINESS_EMAILS.SUPPORT_EMAIL,
      subject: `🚚 Driver Assignment Completed - ${shipment.displayId || shipment.id}`,
      
      // Use driver template but with admin context
      driver_name: 'Admin Team',
      driver_email_address: BUSINESS_EMAILS.ADMIN_EMAIL,
      driver_phone_number: 'System Notification',
      
      // Shipment details for admin reference
      shipment_id: shipment.displayId || shipment.id,
      customer_name: shipment.userName || 'Customer',
      customer_phone: shipment.phone || 'Not provided',
      customer_email_address: shipment.email || 'Not provided',
      pickup_address: shipment.pickupAddress || 'Address not provided',
      delivery_address: shipment.dropoffAddress || 'Address not provided',
      item_description: shipment.itemDescription || 'Item not specified',
      special_instructions: `Assigned to: ${driver.name} (${driver.email}) - ${driver.phone}`,
      
      // Provide defaults for all required template variables
      pickup_date_range: 'Admin Notification',
      delivery_date_range: 'Admin Notification',
      weight: 'N/A - Admin View',
      dimensions: 'N/A - Admin View',
      quantity: '1',
      is_urgent: false,
      
      // Admin-specific information
      driver_license: `Driver: ${driver.licenseNumber || 'Not provided'}`,
      vehicle_information: `Vehicle: ${driver.vehicleInfo || 'Not provided'}`,
      signature_link: `${window.location.origin}/admin/shipments/${shipment.id}`,
      tracking_url: `${window.location.origin}/admin/tracking/${shipment.id}`,
      estimated_delivery_date: shipment.estimatedDelivery || 'To be determined'
    };

    const result = await emailjs.send(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.TEMPLATE_ID_DRIVER_ASSIGNMENT,
      emailParams
    );

    console.log('✅ ADMIN NOTIFICATION SENT SUCCESSFULLY');
    return true;

  } catch (error) {
    console.error('❌ ADMIN NOTIFICATION FAILED:', error);
    console.log('📋 ADMIN NOTIFICATION FALLBACK - Assignment logged');
    return false;
  }
};

// ✅ TEST: Fixed driver assignment email
export const testFixedDriverAssignment = async () => {
  const testDriver = {
    name: 'Michael Rodriguez',
    email: 'contact@flexlogisticsmanagement.com',
    phone: '(555) 987-6543',
    licenseNumber: 'DL987654321',
    vehicleInfo: '2022 Ford Transit - License: TRN123'
  };

  const testShipment = {
    id: 'shipment_123456',
    displayId: 'SH123456',
    itemDescription: 'Office furniture and equipment',
    pickupAddress: '123 Business Ave, Seattle, WA 98101',
    dropoffAddress: '456 Corporate Blvd, Bellevue, WA 98004',
    userName: 'Sarah Johnson',
    phone: '(555) 123-4567',
    email: 'sarah.johnson@example.com',
    specialInstructions: 'Call 30 minutes before arrival',
    estimatedDelivery: '2024-12-20',
    pickupDateRange: 'December 15-16, 2024',
    deliveryDateRange: 'December 19-20, 2024',
    weight: '250 lbs',
    dimensions: '48x40x30 inches',
    quantity: '5 pieces',
    isUrgent: false
  };

  const testSignatureLink = `${window.location.origin}/driver-signature/shipment_123456?token=test_token_123&driverId=driver_789`;

  console.log('🧪 TESTING FIXED DRIVER ASSIGNMENT');
  
  try {
    const result = await sendDriverAssignmentEmail(testDriver, testShipment, testSignatureLink);
    console.log('✅ FIXED VERSION TEST COMPLETE');
    return result;
  } catch (error) {
    console.log('❌ FIXED VERSION TEST FAILED');
    return false;
  }
};

// EXISTING FUNCTIONS (keep these as they are)
export const submitQuoteRequest = async (quoteData) => {
  try {
    console.log('🚀 SUBMITTING QUOTE REQUEST');
    console.log('Customer:', quoteData.name, `(${quoteData.email})`);

    const quoteRef = await addDoc(collection(db, 'quoteRequests'), {
      ...quoteData,
      status: 'pending',
      createdAt: serverTimestamp(),
      read: false
    });

    console.log('✅ Firestore saved - ID:', quoteRef.id);
    
    const customerEmailSuccess = await sendQuoteCustomerConfirmation(quoteData, quoteRef.id);
    
    console.log('📧 ADMIN NOTIFICATION - New quote received');
    console.log('From:', quoteData.name, `(${quoteData.email})`);
    console.log('Quote ID:', quoteRef.id);

    return { 
      success: true, 
      id: quoteRef.id,
      emails: {
        customer: customerEmailSuccess
      }
    };
    
  } catch (error) {
    console.error('❌ Submission failed:', error);
    return { success: false, error: error.message };
  }
};

// Send auto-reply confirmation to customer
const sendQuoteCustomerConfirmation = async (quoteData, quoteId) => {
  try {
    console.log('📨 PREPARING CUSTOMER EMAIL');
    console.log('Recipient:', quoteData.email);
    
    const emailParams = {
      to_email: quoteData.email,
      to_name: quoteData.name,
      from_name: 'Flex Logistics Management',
      from_email: BUSINESS_EMAILS.FROM_EMAIL,
      reply_to: BUSINESS_EMAILS.SUPPORT_EMAIL,
      subject: `Quote Request Received - ${quoteId}`,
      
      company: quoteData.company || 'Not provided',
      phone: quoteData.phone || 'Not provided',
      origin: quoteData.origin || 'Not provided',
      destination: quoteData.destination || 'Not provided',
      package_type: quoteData.packageType || 'Not specified',
      weight: quoteData.weight || 'Not provided',
      dimensions: quoteData.dimensions || 'Not provided',
      special_requirements: quoteData.specialRequirements || 'None',
      quote_id: quoteId,
      timestamp: new Date().toLocaleString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    };

    console.log('🔧 Sending email with professional template');
    console.log('Template used:', EMAILJS_CONFIG.TEMPLATE_ID_QUOTE_CUSTOMER);

    const result = await emailjs.send(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.TEMPLATE_ID_QUOTE_CUSTOMER,
      emailParams
    );

    console.log('✅ CUSTOMER EMAIL SENT SUCCESSFULLY');
    return true;

  } catch (error) {
    console.error('❌ CUSTOMER EMAIL FAILED:');
    console.error('Error:', error);
    return false;
  }
};

// 🧪 TEST FUNCTION (existing)
export const testCustomerEmail = async () => {
  const testData = {
    name: 'Sarah Johnson',
    email: 'sarah.johnson@example.com',
    company: 'Johnson Enterprises',
    phone: '(555) 012-3456',
    origin: 'Seattle, WA',
    destination: 'Miami, FL',
    packageType: 'freight',
    weight: '500 kg',
    dimensions: '120x80x60 cm',
    specialRequirements: 'Urgent delivery needed'
  };

  console.log('🧪 TESTING WITH REALISTIC DATA');
  
  try {
    await sendQuoteCustomerConfirmation(testData, 'TEST-456');
    console.log('✅ TEST COMPLETE - Check customer email for professional confirmation');
    return true;
  } catch (error) {
    console.log('❌ TEST FAILED');
    return false;
  }
};

// Existing shipment functions (keep as-is)
export const sendEmailNotification = async (shipmentId, type) => {
  try {
    const notificationData = {
      notificationSent: true,
      lastNotification: new Date(),
      notificationType: type
    };
    await updateDoc(doc(db, 'shipments', shipmentId), notificationData);
    console.log(`Email notification ${type} sent for shipment ${shipmentId}`);
    return true;
  } catch (error) {
    console.error('Error sending email notification:', error);
    return false;
  }
};

export const notifyNewShipment = (shipmentId) => {
  return sendEmailNotification(shipmentId, 'new_shipment');
};

export const notifyPaymentReceived = (shipmentId) => {
  return sendEmailNotification(shipmentId, 'payment_received');
};

export const notifyBOLGenerated = (shipmentId) => {
  return sendEmailNotification(shipmentId, 'bol_generated');
};

export const getQuoteRequests = async () => {
  try {
    console.log('Fetching quote requests from Firestore...');
    return { success: true, data: [] };
  } catch (error) {
    console.error('Error fetching quote requests:', error);
    return { success: false, error: error.message };
  }
};

// Make test functions available globally for debugging
if (typeof window !== 'undefined') {
  window.testCustomerEmail = testCustomerEmail;
  window.testFixedDriverAssignment = testFixedDriverAssignment;
  window.sendDriverAssignment = sendDriverAssignment;
}